import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { Notification, FollowRequest, Bookmark, UserProfile } from '@/types';

interface SocialState {
  notifications: Notification[];
  unreadCount: number;
  followRequests: FollowRequest[];
  bookmarks: Bookmark[];
  followers: UserProfile[];
  following: UserProfile[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SocialState = {
  notifications: [],
  unreadCount: 0,
  followRequests: [],
  bookmarks: [],
  followers: [],
  following: [],
  isLoading: false,
  error: null,
};

// Notifications
export const fetchNotifications = createAsyncThunk(
  'social/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/social/notifications');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch notifications');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'social/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/social/notifications/unread-count');
      return response.data.count;
    } catch (error) {
      return rejectWithValue('Failed to fetch unread count');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'social/markNotificationRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await api.put(`/social/notifications/${notificationId}/read`);
      return notificationId;
    } catch (error) {
      return rejectWithValue('Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'social/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      await api.put('/social/notifications/read-all');
      return true;
    } catch (error) {
      return rejectWithValue('Failed to mark all notifications as read');
    }
  }
);

// Follow Requests
export const fetchFollowRequests = createAsyncThunk(
  'social/fetchFollowRequests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/social/follow-requests');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch follow requests');
    }
  }
);

export const approveFollowRequest = createAsyncThunk(
  'social/approveFollowRequest',
  async (requestId: string, { rejectWithValue }) => {
    try {
      await api.post(`/social/follow-requests/${requestId}/approve`);
      return requestId;
    } catch (error) {
      return rejectWithValue('Failed to approve follow request');
    }
  }
);

export const rejectFollowRequest = createAsyncThunk(
  'social/rejectFollowRequest',
  async (requestId: string, { rejectWithValue }) => {
    try {
      await api.post(`/social/follow-requests/${requestId}/reject`);
      return requestId;
    } catch (error) {
      return rejectWithValue('Failed to reject follow request');
    }
  }
);

// Bookmarks
export const fetchBookmarks = createAsyncThunk(
  'social/fetchBookmarks',
  async (entityType: string | undefined, { rejectWithValue }) => {
    try {
      const params = entityType ? `?type=${entityType}` : '';
      const response = await api.get(`/social/bookmarks${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch bookmarks');
    }
  }
);

export const addBookmark = createAsyncThunk(
  'social/addBookmark',
  async ({ entityType, entityId }: { entityType: string; entityId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/social/bookmarks', { entityType, entityId });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to add bookmark');
    }
  }
);

export const removeBookmark = createAsyncThunk(
  'social/removeBookmark',
  async ({ entityType, entityId }: { entityType: string; entityId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/social/bookmarks/${entityType}/${entityId}`);
      return { entityType, entityId };
    } catch (error) {
      return rejectWithValue('Failed to remove bookmark');
    }
  }
);

// Followers/Following
export const fetchFollowers = createAsyncThunk(
  'social/fetchFollowers',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/social/users/${userId}/followers`);
      return response.data.followers;
    } catch (error) {
      return rejectWithValue('Failed to fetch followers');
    }
  }
);

export const fetchFollowing = createAsyncThunk(
  'social/fetchFollowing',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/social/users/${userId}/following`);
      return response.data.following;
    } catch (error) {
      return rejectWithValue('Failed to fetch following');
    }
  }
);

export const followUser = createAsyncThunk(
  'social/followUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/social/users/${userId}/follow`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to follow user');
    }
  }
);

export const unfollowUser = createAsyncThunk(
  'social/unfollowUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/social/users/${userId}/follow`);
      return userId;
    } catch (error) {
      return rejectWithValue('Failed to unfollow user');
    }
  }
);

const socialSlice = createSlice({
  name: 'social',
  initialState,
  reducers: {
    clearSocialState: () => initialState,
    decrementUnreadCount: (state) => {
      if (state.unreadCount > 0) state.unreadCount -= 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(n => n.isRead = true);
        state.unreadCount = 0;
      })
      // Follow Requests
      .addCase(fetchFollowRequests.fulfilled, (state, action) => {
        state.followRequests = action.payload;
      })
      .addCase(approveFollowRequest.fulfilled, (state, action) => {
        state.followRequests = state.followRequests.filter(r => r.id !== action.payload);
      })
      .addCase(rejectFollowRequest.fulfilled, (state, action) => {
        state.followRequests = state.followRequests.filter(r => r.id !== action.payload);
      })
      // Bookmarks
      .addCase(fetchBookmarks.fulfilled, (state, action) => {
        state.bookmarks = action.payload.bookmarks;
      })
      .addCase(addBookmark.fulfilled, (state, action) => {
        state.bookmarks.unshift(action.payload);
      })
      .addCase(removeBookmark.fulfilled, (state, action) => {
        state.bookmarks = state.bookmarks.filter(
          b => !(b.entityType === action.payload.entityType && b.entityId === action.payload.entityId)
        );
      })
      // Followers/Following
      .addCase(fetchFollowers.fulfilled, (state, action) => {
        state.followers = action.payload;
      })
      .addCase(fetchFollowing.fulfilled, (state, action) => {
        state.following = action.payload;
      });
  },
});

export const { clearSocialState, decrementUnreadCount } = socialSlice.actions;
export default socialSlice.reducer;
